<template>
  <v-dialog v-model="dialog" max-width="1280px" width="95%" persistent content-class="solicitud-nivelacion-dialog">
    <v-card class="dialog-card">
      
      <!-- Header de Diálogo -->
      <v-card-title class="dialog-header px-6 py-5 d-flex align-center justify-space-between border-bottom">
        <div class="d-flex align-center">
          <div class="filter-header-icon mr-3">
            <v-icon color="primary" size="22">mdi-file-search-outline</v-icon>
          </div>
          <div>
            <div class="text-h6 font-weight-bold slate-900--text" style="line-height: 1.2;">
              {{ soloLectura ? 'Ver Solicitud de Nivelación' : 'Revisar Solicitud de Nivelación' }}
            </div>
            <div class="text-caption grey--text text--darken-1 mt-0.5">Solicitud N° {{ solicitud?.id || '-' }}</div>
          </div>
        </div>
        <div class="d-flex align-center gap-2">
          <span v-if="solicitud" :class="['custom-badge', 'badge-xs', 'mr-2', getEstadoClass(solicitud.estadoSolicitud)]">
            {{ estadoLabel(solicitud.estadoSolicitud) }}
          </span>
          <v-btn icon color="grey darken-1" :disabled="revisando" @click="cerrar" small class="close-btn">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </v-card-title>

      <div class="dialog-body pa-6">
        <div v-if="cargando" class="d-flex justify-center py-8">
          <v-progress-circular indeterminate color="primary" size="36" width="3" />
        </div>

        <template v-else-if="solicitud">
          <v-alert
            v-if="errorRevision.length"
            type="error"
            dense
            outlined
            dismissible
            class="mb-4"
            @input="errorRevision = []">
            <ul class="mb-0 pl-4">
              <li v-for="(error, index) in errorRevision" :key="index">{{ error }}</li>
            </ul>
          </v-alert>

          <div class="dialog-layout-row">
            
            <!-- Columna Izquierda (40%) -->
            <div class="dialog-col-left">
              <!-- Detalle Grid -->
              <div class="detalle-grid mb-4">
                <div class="detalle-item">
                  <span class="detalle-label">Estudiante</span>
                  <span class="detalle-value">{{ solicitud.estudiante?.nombreCompleto || '—' }}</span>
                  <small v-if="solicitud.estudiante?.ci" class="detalle-helper">CI: {{ solicitud.estudiante.ci }}</small>
                </div>
                <div class="detalle-item">
                  <span class="detalle-label">Programa / Sede</span>
                  <span class="detalle-value">{{ solicitud.programa?.nombreCompuesto || '—' }}</span>
                  <small class="detalle-helper">
                    <span v-if="solicitud.programa?.codigo">{{ solicitud.programa.codigo }}</span>
                    <span v-if="solicitud.programa?.codigo && solicitud.sede?.nombre"> · </span>
                    <span v-if="solicitud.sede?.nombre">{{ solicitud.sede.nombre }}</span>
                  </small>
                </div>
                <div class="detalle-item">
                  <span class="detalle-label">Módulo</span>
                  <span class="detalle-value">
                    <span v-if="solicitud.programaModulo" class="font-weight-bold">
                      M{{ solicitud.programaModulo.numero }} - {{ solicitud.programaModulo.moduloNombre }}
                    </span>
                    <span v-else>—</span>
                  </span>
                </div>
                <div class="detalle-item">
                  <span class="detalle-label">Nota</span>
                  <span class="detalle-value nota-value">{{ solicitud.nota }}</span>
                  <small v-if="solicitud.notaAnterior !== null" class="detalle-helper">antes {{ solicitud.notaAnterior }}</small>
                </div>
                <div class="detalle-item">
                  <span class="detalle-label">Solicitado por</span>
                  <span v-if="solicitud.solicitadoPor" class="detalle-value">
                    {{ solicitud.solicitadoPor.nombreCompleto }}
                  </span>
                  <span v-else class="detalle-value">—</span>
                  <small class="detalle-helper">
                    <v-icon x-small color="grey lighten-1" class="mr-0.5">mdi-calendar-clock</v-icon>
                    {{ formatFecha(solicitud.fechaSolicitud) }}
                  </small>
                </div>
                <div class="detalle-item">
                  <span class="detalle-label">Revisado por</span>
                  <span v-if="solicitud.revisadoPor" class="detalle-value">
                    {{ solicitud.revisadoPor.nombreCompleto }}
                  </span>
                  <span v-else class="detalle-value no-revisor">Sin revisar</span>
                  <small v-if="solicitud.revisadoPor" class="detalle-helper">
                    <v-icon x-small color="grey lighten-1" class="mr-0.5">mdi-calendar-check</v-icon>
                    {{ formatFecha(solicitud.fechaRevision) }}
                  </small>
                </div>
              </div>

              <div v-if="solicitud.pagosInscripcionId" class="mb-4">
                <v-btn
                  depressed
                  outlined
                  color="primary"
                  class="rounded-lg action-btn px-4 w-100"
                  style="height: 36px !important; font-size: 0.8rem;"
                  @click="verPagoInscripcion">
                  <v-icon small left>mdi-cash-check</v-icon>
                  Ver pago de inscripción seleccionado
                </v-btn>
              </div>

              <!-- Motivo -->
              <v-card class="text-card mb-4" elevation="0" outlined>
                <div class="text-card__title">Motivo</div>
                <p class="text-card__body">{{ solicitud.motivo || 'Sin motivo registrado' }}</p>
              </v-card>

              <!-- Observación de revisión -->
              <v-card v-if="solicitud.observacionRevision" class="text-card mb-4" elevation="0" outlined>
                <div class="text-card__title text-error">Observación de revisión</div>
                <p class="text-card__body">{{ solicitud.observacionRevision }}</p>
              </v-card>

              <!-- Cuadro de observaciones en modo edición -->
              <div v-if="mostrarAcciones" class="mt-4">
                <span class="input-label">Observación (requerida para rechazar)</span>
                <v-textarea
                  v-model="observacion"
                  placeholder="Ingrese el motivo o aclaración de la revisión..."
                  outlined
                  dense
                  auto-grow
                  rows="2"
                  hide-details
                  class="rounded-lg custom-input mt-1" />
              </div>
            </div>

            <!-- Columna Derecha (60%) -->
            <div class="dialog-col-right">
              <!-- Documentación PDF preview -->
              <v-card v-if="documentoPreviewUrl" class="pdf-preview-card" elevation="0" outlined>
                <div class="pdf-preview-header px-4 py-2">
                  <span class="pdf-preview-title">
                    <v-icon x-small color="primary" class="mr-1">mdi-file-pdf-box</v-icon>
                    Documentación de Respaldo
                  </span>
                </div>
                <iframe :src="documentoPreviewUrl" class="pdf-preview-frame" title="Vista previa PDF" />
              </v-card>
              
              <v-card v-else-if="solicitud.documento" class="text-card h-100" elevation="0" outlined>
                <div class="text-card__title">Documentación</div>
                <p class="text-card__body">{{ solicitud.documento }}</p>
              </v-card>
            </div>

          </div>
        </template>
      </div>

      <!-- Acciones de Diálogo -->
      <v-card-actions v-if="mostrarAcciones" class="dialog-actions px-6 py-4 bg-light d-flex justify-end gap-2 border-top">
        <v-btn
          depressed
          outlined
          color="error"
          class="rounded-lg action-btn px-6"
          :loading="revisando === 'rechazado'"
          :disabled="!!revisando || !observacion.trim()"
          @click="confirmarRevision('rechazado')">
          <v-icon small left>mdi-close</v-icon>
          Rechazar
        </v-btn>
        <v-btn
          depressed
          color="success"
          class="rounded-lg action-btn apply-btn px-6"
          :loading="revisando === 'aprobado'"
          :disabled="!!revisando"
          @click="confirmarRevision('aprobado')">
          <v-icon small left>mdi-check</v-icon>
          Aprobar
        </v-btn>
      </v-card-actions>
      <v-card-actions v-else class="dialog-actions px-6 py-4 bg-light d-flex justify-end border-top">
        <v-btn
          depressed
          outlined
          class="rounded-lg action-btn cancel-btn px-6"
          @click="cerrar">
          Cerrar
        </v-btn>
      </v-card-actions>
    </v-card>

    <dialog-ver-pago-inscripcion ref="dialogVerPago" />

    <!-- Advertencias de sincronización con universidad -->
    <v-dialog v-model="dialogAdvertencias" max-width="480px" content-class="rounded-lg">
      <v-card>
        <v-card-title class="d-flex align-center px-6 py-4 border-bottom">
          <v-icon color="warning" class="mr-2">mdi-alert-outline</v-icon>
          <span class="text-h6 font-weight-bold">Advertencias</span>
        </v-card-title>
        <v-card-text class="px-6 py-4">
          <ul class="advertencias-list">
            <li v-for="(advertencia, index) in advertencias" :key="index">{{ advertencia }}</li>
          </ul>
        </v-card-text>
        <v-card-actions class="px-6 py-4 justify-end border-top">
          <v-btn depressed color="primary" class="rounded-lg action-btn px-6" @click="dialogAdvertencias = false">
            Entendido
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import SolicitudCalificacionNivelacionService from '../../services/solicitud-calificacion-nivelacion.service';
import DialogVerPagoInscripcion from './DialogVerPagoInscripcion.vue';
import {
  ESTADOS_SOLICITUD_CALIFICACION_NIVELACION,
  ESTADOS_SOLICITUD_CALIFICACION_NIVELACION_LABELS,
  ESTADOS_SOLICITUD_CALIFICACION_NIVELACION_COLORES,
} from '../../utils/constantes';

export default {
  name: 'DialogRevisarSolicitudNivelacion',
  components: { DialogVerPagoInscripcion },
  props: {
    puedeRevisarSolicitud: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dialog: false,
      cargando: false,
      revisando: null,
      solicitud: null,
      observacion: '',
      documentoPreviewUrl: null,
      soloLectura: false,
      dialogAdvertencias: false,
      advertencias: [],
      errorRevision: [],
    };
  },
  computed: {
    esPendiente() {
      return this.solicitud?.estadoSolicitud === ESTADOS_SOLICITUD_CALIFICACION_NIVELACION.PENDIENTE;
    },
    mostrarAcciones() {
      return this.esPendiente && !this.soloLectura && this.puedeRevisarSolicitud;
    },
  },
  methods: {
    abrir(solicitudId, soloLectura = false) {
      this.dialog = true;
      this.solicitud = null;
      this.observacion = '';
      this.soloLectura = soloLectura;
      this.errorRevision = [];
      this.limpiarDocumentoPreview();
      this.cargarSolicitud(solicitudId);
    },
    cerrar() {
      this.dialog = false;
      this.solicitud = null;
      this.limpiarDocumentoPreview();
    },
    limpiarDocumentoPreview() {
      if (this.documentoPreviewUrl) {
        URL.revokeObjectURL(this.documentoPreviewUrl);
      }
      this.documentoPreviewUrl = null;
    },
    verPagoInscripcion() {
      if (!this.solicitud?.pagosInscripcionId) return;
      this.$refs.dialogVerPago.abrir(this.solicitud.pagosInscripcionId);
    },
    estadoLabel(estado) {
      return ESTADOS_SOLICITUD_CALIFICACION_NIVELACION_LABELS[estado] || estado;
    },
    estadoColor(estado) {
      return ESTADOS_SOLICITUD_CALIFICACION_NIVELACION_COLORES[estado] || 'grey';
    },
    getEstadoClass(estado) {
      if (!estado) return 'state-default';
      const norm = estado.toUpperCase().trim();
      if (norm === 'APROBADO' || norm === 'APROBADA') return 'state-concluido';
      if (norm === 'GENERADO' || norm === 'PENDIENTE') return 'state-en-desarrollo';
      if (norm === 'RECHAZADO' || norm === 'RECHAZADA') return 'state-no-iniciado';
      return 'state-default';
    },
    formatFecha(fecha) {
      if (!fecha) return '—';
      return new Date(fecha).toLocaleString('es-BO', {
        year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
      });
    },
    async cargarSolicitud(solicitudId) {
      this.cargando = true;
      try {
        const { data } = await SolicitudCalificacionNivelacionService.visualizarSolicitud(solicitudId);
        this.solicitud = data?.datos || null;
        if (this.solicitud?.documentoBase64) {
          const bytes = atob(this.solicitud.documentoBase64);
          const array = new Uint8Array(bytes.length);
          for (let i = 0; i < bytes.length; i += 1) {
            array[i] = bytes.charCodeAt(i);
          }
          const blob = new Blob([array], { type: 'application/pdf' });
          this.documentoPreviewUrl = URL.createObjectURL(blob);
        }
      } catch (error) {
        const mensaje = error?.response?.data?.mensaje || error.message || 'Error al obtener la solicitud';
        this.$notify({ group: 'notifications', title: 'Error', text: mensaje, type: 'error' });
        this.cerrar();
      } finally {
        this.cargando = false;
      }
    },
    async confirmarRevision(estadoSolicitud) {
      const esAprobar = estadoSolicitud === ESTADOS_SOLICITUD_CALIFICACION_NIVELACION.APROBADO;
      const confirmed = await this.$dialog.confirm(
        {
          title: esAprobar ? 'Confirmar aprobación' : 'Confirmar rechazo',
          body: `¿Está seguro de que desea ${esAprobar ? 'aprobar' : 'rechazar'} esta solicitud de nivelación?`,
        },
        {
          okText: 'Sí',
          cancelText: 'No',
          customClass: esAprobar ? 'dialog-confirmar-aprobar' : 'dialog-confirmar-rechazar',
        }
      ).then(() => true).catch(() => false);

      if (!confirmed) return;

      await this.revisar(estadoSolicitud);
    },
    async revisar(estadoSolicitud) {
      if (!this.solicitud?.id) return;
      if (estadoSolicitud === ESTADOS_SOLICITUD_CALIFICACION_NIVELACION.RECHAZADO && !this.observacion) {
        this.$notify({
          group: 'notifications', title: 'Atención', text: 'Debe indicar una observación para rechazar la solicitud', type: 'warning',
        });
        return;
      }

      this.revisando = estadoSolicitud;
      this.errorRevision = [];
      try {
        const { data } = await SolicitudCalificacionNivelacionService.revisar({
          solicitudId: this.solicitud.id,
          estadoSolicitud,
          observacionRevision: this.observacion || undefined,
        });
        this.$notify({
          group: 'notifications',
          title: 'Éxito',
          text: `Solicitud ${estadoSolicitud === ESTADOS_SOLICITUD_CALIFICACION_NIVELACION.APROBADO ? 'aprobada' : 'rechazada'} correctamente`,
          type: 'success',
        });
        const advertencias = data?.datos?.advertencias || [];
        this.$emit('actualizado');
        this.cerrar();
        if (advertencias.length) {
          this.advertencias = advertencias;
          this.dialogAdvertencias = true;
        }
      } catch (error) {
        const mensaje = error?.response?.data?.mensaje || error.message || 'Error al revisar la solicitud';
        this.errorRevision = Array.isArray(mensaje) ? mensaje : [mensaje];
        this.$notify({
          group: 'notifications',
          title: 'Error',
          text: Array.isArray(mensaje) ? mensaje.join(' | ') : mensaje,
          type: 'error',
        });
      } finally {
        this.revisando = null;
      }
    },
  },
};
</script>

<style scoped>
::v-deep .solicitud-nivelacion-dialog {
  overflow: hidden !important;
  max-height: 92vh;
}

.dialog-card {
  border-radius: 16px !important;
  overflow-x: hidden;
  overflow-y: hidden;
  max-height: 92vh;
  max-width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: #ffffff !important;
}

.dialog-header {
  background: #ffffff !important;
  border-bottom: 1px solid #e2e8f0 !important;
}

.filter-header-icon {
  background: #eff6ff;
  border-radius: 10px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #dbeafe;
}

.slate-900--text {
  color: #0f172a !important;
}

.dialog-body {
  flex: 1 1 auto;
  overflow-y: auto;
  min-height: 0;
  background: #ffffff !important;
}

.dialog-actions {
  flex: 0 0 auto;
  border-top: 1px solid #e2e8f0;
  background-color: #f8fafc;
}

/* Detail grid */
.detalle-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.detalle-item {
  display: flex;
  flex-direction: column;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px 14px;
  transition: all 0.2s ease;
}

.detalle-item:hover {
  border-color: #cbd5e1;
  background-color: #f1f5f9;
}

.detalle-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.detalle-value {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1e293b;
  margin-top: 2px;
}

.nota-value {
  font-size: 1.1rem;
  font-weight: 700;
  color: #0f172a;
}

.detalle-helper {
  color: #64748b;
  font-size: 0.75rem;
  margin-top: 3px;
  display: flex;
  align-items: center;
  font-weight: 500;
}

.no-revisor {
  color: #94a3b8;
  font-style: italic;
}

/* Card details design */
.text-card {
  padding: 12px 16px;
  border-radius: 12px !important;
  border: 1px solid #e2e8f0 !important;
  background: #ffffff;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.02);
}

.text-card__title {
  font-weight: 700;
  font-size: 0.75rem;
  color: #2563eb;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 4px;
}

.text-card__body {
  margin: 0;
  font-size: 0.85rem;
  color: #334155;
  line-height: 1.4;
  white-space: pre-line;
}

/* 2-Column Row Layout */
.dialog-layout-row {
  display: flex;
  gap: 24px;
  align-items: stretch;
}

.dialog-col-left {
  flex: 4;
  min-width: 320px;
}

.dialog-col-right {
  flex: 6;
  min-width: 480px;
  display: flex;
  flex-direction: column;
}

@media (max-width: 960px) {
  .dialog-layout-row {
    flex-direction: column;
  }
  .dialog-col-left, .dialog-col-right {
    width: 100%;
    min-width: 0;
  }
}

/* PDF Preview Frame */
.pdf-preview-card {
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #cbd5e1 !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 550px;
}

.pdf-preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.pdf-preview-title {
  font-size: 0.75rem;
  font-weight: 700;
  color: #2563eb;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  display: flex;
  align-items: center;
}

.pdf-preview-frame {
  width: 100%;
  flex: 1 1 auto;
  border: none;
  background-color: #ffffff;
  min-height: 500px;
}

/* Custom inputs style */
.input-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #475569;
  display: inline-block;
}

.text-error {
  color: #ef4444 !important;
}

.custom-input ::v-deep .v-input__slot {
  background-color: #f8fafc !important;
  border: 1px solid #cbd5e1 !important;
  border-radius: 8px !important;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: none !important;
  min-height: 40px !important;
}

.custom-input ::v-deep .v-input__slot:hover {
  background-color: #f1f5f9 !important;
  border-color: #94a3b8 !important;
}

.custom-input ::v-deep .v-input__slot:focus-within {
  background-color: #ffffff !important;
  border-color: #2563eb !important;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15) !important;
}

.custom-input ::v-deep fieldset {
  border: none !important;
}

.custom-input ::v-deep .v-input__prepend-inner {
  margin-top: 8px !important;
}

.custom-input ::v-deep .v-input__append-inner {
  margin-top: 8px !important;
}

.custom-input ::v-deep .v-icon {
  color: #64748b !important;
  font-size: 20px !important;
}

.custom-input ::v-deep textarea {
  color: #1e293b !important;
  font-size: 0.875rem !important;
  font-weight: 500 !important;
}

/* Buttons style */
.action-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.2px !important;
  height: 40px !important;
  border-radius: 8px !important;
}

.apply-btn {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%) !important;
  color: #ffffff !important;
  box-shadow: 0 4px 6px -1px rgba(37, 99, 235, 0.2), 0 2px 4px -1px rgba(37, 99, 235, 0.1) !important;
  transition: all 0.2s ease !important;
}

.apply-btn:hover {
  box-shadow: 0 6px 8px -1px rgba(37, 99, 235, 0.25), 0 3px 6px -1px rgba(37, 99, 235, 0.15) !important;
  transform: translateY(-1px);
}

.apply-btn.v-btn--disabled {
  background: #e2e8f0 !important;
  color: #94a3b8 !important;
  box-shadow: none !important;
  border-color: #e2e8f0 !important;
}

.cancel-btn {
  border: 1px solid #cbd5e1 !important;
  color: #475569 !important;
  background-color: #ffffff !important;
}

.cancel-btn:hover {
  background-color: #f8fafc !important;
  color: #1e293b !important;
}

.close-btn {
  transition: transform 0.2s ease;
}

.close-btn:hover {
  transform: rotate(90deg);
}

/* Badges for status */
.custom-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 9999px;
  line-height: 1.25;
  white-space: nowrap;
}

.badge-xs {
  font-size: 0.65rem;
  padding: 1px 6px;
}

.state-concluido {
  background-color: #e8f5e9;
  color: #2e7d32;
  border: 1px solid rgba(46, 125, 50, 0.2);
}

.state-en-desarrollo {
  background-color: #fff3e0;
  color: #ef6c00;
  border: 1px solid rgba(239, 108, 0, 0.2);
}

.state-no-iniciado {
  background-color: #ffebee;
  color: #c62828;
  border: 1px solid rgba(198, 40, 40, 0.2);
}

.state-default {
  background-color: #eceff1;
  color: #455a64;
  border: 1px solid rgba(69, 90, 100, 0.2);
}

.gap-2 { gap: 8px; }
.bg-light { background-color: #f8fafc; }

.advertencias-list {
  margin: 0;
  padding-left: 20px;
  color: #7c4a00;
}

.advertencias-list li {
  font-size: 0.875rem;
  line-height: 1.5;
  margin-bottom: 6px;
}

.advertencias-list li:last-child {
  margin-bottom: 0;
}
</style>

<style>
/* Vuejs-dialog renders outside this component, so these rules are intentionally global */
.dialog-confirmar-aprobar .dg-content {
  border-top: 4px solid #2e7d32;
}

.dialog-confirmar-rechazar .dg-content {
  border-top: 4px solid #c62828;
}
</style>
